import PropTypes from 'prop-types';

// Types pour les champs de date décomposés
const dateFieldPropType = PropTypes.shape({
    day: PropTypes.string,
    month: PropTypes.string,
    year: PropTypes.string
});

// Types pour les champs de signature
const signaturePropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
        lieu: PropTypes.string,
        date: dateFieldPropType,
        oppositionProspection: PropTypes.oneOf(['0', '1'])
    })
]);

export const vehiculeDataPropType = PropTypes.shape({
    // Champs communs
    etat: PropTypes.oneOf(['VN', 'VO']), // État du véhicule (VN: véhicule neuf, VO: véhicule occasion)
    veh_33: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Nuance (0: clair, 1: foncé)
    veh_34: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']),
        PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9])
    ]), // Couleur dominante (0: noir, 1: marron, 2: bleu, 3: rouge, 4: beige, 5: orange, 6: gris, 7: jaune, 8: vert, 9: blanc)

    // Étape 1 - Certification de conformité et identification du véhicule
    ccv_1: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Véhicule (0: constructeur, 1: representant accrédité)
    ccs_1: PropTypes.string, // Nom et prénom
    ccs_2: dateFieldPropType, // Date de réception par type
    ccs_3: PropTypes.string, // Sous le n° (K)

    // Champs véhicule neuf
    veh_0: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Disposez-vous d'un certificat de conformité Européen (CoC) sous forme électronique ?
    veh_1: PropTypes.string, // Marque (D.1)
    veh_2: PropTypes.string, // Type, variante, version (D.2)
    veh_3: PropTypes.string, // C.N.I.T (D.2.1)
    veh_4: PropTypes.string, // Dénomination commerciale (D.3)
    veh_5: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Usage (0: non, 1: oui)
    veh_6: PropTypes.string, // N° identification du véhicule (E)
    veh_7: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Masse en charge max. tech. adm. (F.1)
    veh_8: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Masse ch. max. adm. dans l'Etat membre (F.2)
    veh_9: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Masse en charge max. de l'ensemble (F.3)
    veh_10: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Masse en service (G)
    veh_11: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Poids à vide national (G.1)
    veh_12: PropTypes.string, // Catégorie (J)
    veh_13: PropTypes.string, // Genre national (J.1)
    veh_14: PropTypes.string, // Carrosserie CE (J.2)
    veh_15: PropTypes.string, // Carr. nationale (J.3)
    veh_16: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Cylindrée (P.1)
    veh_17: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Puiss. nette maxi (P.2)
    veh_18: PropTypes.string, // Type de carburant ou source d'énergie (P.3)
    veh_19: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Puissance administrative nationale (P.6)
    veh_20: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Rapp. puiss / Masse (motocycles) (Q)
    veh_21: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Places assises (S.1)
    veh_22: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Places debout (S.2)
    veh_23: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Niveau sonore (U.1)
    veh_24: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Vitesse du moteur (en m-1) (U.2)
    veh_25: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Co2 (en g/km) (V.7)
    veh_26: PropTypes.string, // Classe environnementale (V.9)

    // Étape 2 - Certificat de vente et identité du titulaire
    vde_1: PropTypes.string, // Société
    vde_2: dateFieldPropType, // Date de la vente du véhicule

    // Identité du titulaire
    tit_1: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Vous êtes (0: personne physique, 1: personne morale)
    tit_2: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Sexe (0: homme, 1: femme)
    tit_3: PropTypes.string, // Nom de naissance et prénom
    tit_4: PropTypes.string, // Nom d'usage / d'époux.se
    tit_5: dateFieldPropType, // Né.e le
    tit_6: PropTypes.string, // Commune de naissance
    tit_7: PropTypes.string, // Département de naissance
    
    tit_8: PropTypes.string, // Pays de naissance
    tit_9: PropTypes.string, // Adresse mail
    tit_10: PropTypes.string, // Raison sociale
    tit_11: PropTypes.string, // N° SIRET

    // Co-titulaire
    cotit_0: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Le véhicule est-il dans un cas de multi-propriété ?
    cotit_1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Nombre de titulaires
    cotit_2: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Le co-titulaire est (0: personne physique, 1: personne morale)
    cotit_3: PropTypes.string, // Nom de naissance et prénom
    cotit_4: PropTypes.string, // Nom d'usage / d'époux.se
    cotit_5: PropTypes.string, // Raison sociale

    // Étape 3 - Location
    loc_0: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Le véhicule est-il pris en location ?
    loc_1: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Type de location (0: courte durée, 1: longue durée)
    loc_3: PropTypes.string, // Raison sociale de la société de location
    loc_4: PropTypes.string, // N° SIRET de la société de location
    loc_5: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Le locataire est (0: personne physique, 1: personne morale)
    loc_6: PropTypes.string, // Nom et prénom du locataire
    loc_7: PropTypes.string, // Raison sociale du locataire
    loc_8: PropTypes.string, // N° SIRET du locataire

    // Adresse du locataire
    loc_9: PropTypes.string, // N° de la voie
    loc_10: PropTypes.string, // Complément
    loc_11: PropTypes.string, // Type de voie
    loc_12: PropTypes.string, // Nom de la voie
    loc_13: PropTypes.string, // Code postal
    loc_14: PropTypes.string, // Commune
    loc_15: PropTypes.string, // Téléphone portable
    loc_16: PropTypes.string, // Adresse mail

    // Étape 4 - Signatures
    sig_1: signaturePropType, // Signature du titulaire
    sig_2: signaturePropType, // Signature du loueur
    sig_3: signaturePropType, // Signature du locataire
    sig_4: signaturePropType, // Signature de l'ancien propriétaire/concession

    // Champs véhicule occasion
    occ_ra_1: PropTypes.oneOfType([
        PropTypes.oneOf(['1', '2', '3', '4', '5', '6']),
        PropTypes.oneOf([1, 2, 3, 4, 5, 6])
    ]), // Motif de la demande (1: Certificat, 2: Duplicata, 3: Correction, 4: Changement de domicile, 5: Changement d'état civil, 6: Changement de caractéristiques)
    occ_veh_27: PropTypes.string, // N° d'immatriculation du véhicule (A)
    occ_veh_27_1: PropTypes.string, // Date d'achat - Jour
    occ_veh_27_2: PropTypes.string, // Date d'achat - Mois
    occ_veh_27_3: PropTypes.string, // Date d'achat - Année
    occ_veh_28_1: PropTypes.string, // Date de certificat actuel (I) - Jour
    occ_veh_28_2: PropTypes.string, // Date de certificat actuel (I) - Mois
    occ_veh_28_3: PropTypes.string, // Date de certificat actuel (I) - Année
    occ_veh_29_1: PropTypes.string, // Date de 1ère immatriculation (B) - Jour
    occ_veh_29_2: PropTypes.string, // Date de 1ère immatriculation (B) - Mois
    occ_veh_29_3: PropTypes.string, // Date de 1ère immatriculation (B) - Année
    occ_veh_0: PropTypes.string, // N° de formule du certificat d'immatriculation
    occ_veh_30: PropTypes.string, // N° d'exploitation agricole
    occ_veh_31: PropTypes.string, // Kilométrage inscrit au compteur
    occ_veh_32: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Présence du certificat d'immatriculation (0: non, 1: oui)
    occ_veh_32_1: PropTypes.oneOfType([
        PropTypes.oneOf(['1', '2']),
        PropTypes.oneOf([1, 2])
    ]), // Format du n° d'immatriculation (1: AB-123-CD, 2: 123-AB-45)
    occ_veh_32_2: PropTypes.string, // Motif d'absence de certificat d'immatriculation
    occ_veh_32_3: PropTypes.string, // Numéro de châssis
    occ_veh_32_4: PropTypes.string, // Numéro de moteur

    // Champs véhicule occasion - Ancien propriétaire
    occ_app_1: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Type (0: personne physique, 1: personne morale)
    occ_app_2: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Sexe (0: masculin, 1: féminin)
    occ_app_3: PropTypes.string, // Nom et prénom
    occ_app_4: PropTypes.string, // Raison sociale
    occ_app_5: PropTypes.string, // N° SIRET
    occ_app_5_2: PropTypes.string, // N° SIREN
    occ_app_6: PropTypes.string, // N° de la voie
    occ_app_7: PropTypes.string, // Complément (bis, ter,..)
    occ_app_8: PropTypes.string, // Type de voie
    occ_app_9: PropTypes.string, // Nom de la voie
    occ_app_10: PropTypes.string, // Code postal
    occ_app_11: PropTypes.string, // Commune
    occ_app_12: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Type de cession (0: céder, 1: céder pour destruction)
    occ_app_13_1: PropTypes.string, // Date de cession - Jour
    occ_app_13_2: PropTypes.string, // Date de cession - Mois
    occ_app_13_3: PropTypes.string, // Date de cession - Année
    occ_app_14_1: PropTypes.string, // Heure de cession - Heures
    occ_app_14_2: PropTypes.string, // Heure de cession - Minutes
    occ_app_15: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Certification remise certificat
    occ_app_16: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Certification non transformation
    occ_app_17: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Certification cession pour destruction

    // Champs véhicule occasion - Titulaire (Nouveau propriétaire)
    occ_tit_1: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Type (0: personne physique, 1: personne morale)
    occ_tit_2: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Sexe (0: masculin, 1: féminin)
    occ_tit_3: PropTypes.string, // Nom et prénom
    occ_tit_4: PropTypes.string, // Nom d'usage
    occ_tit_5_1: PropTypes.string, // Date de naissance - Jour
    occ_tit_5_2: PropTypes.string, // Date de naissance - Mois
    occ_tit_5_3: PropTypes.string, // Date de naissance - Année
    occ_tit_6: PropTypes.string, // Commune de naissance
    occ_tit_7: PropTypes.string, // Département de naissance
    occ_tit_8: PropTypes.string, // Pays de naissance
    occ_tit_9: PropTypes.string, // Raison sociale
    occ_tit_10: PropTypes.string, // N° SIRET
    occ_tit_11: PropTypes.string, // N° SIREN
    occ_tit_12: PropTypes.string, // Etage / Escalier / Appartement
    occ_tit_13: PropTypes.string, // Immeuble / Résidence / Bâtiment
    occ_tit_14: PropTypes.string, // N° de la voie
    occ_tit_15: PropTypes.string, // Extension (bis, ter, ..)
    occ_tit_16: PropTypes.string, // Type de voie
    occ_tit_17: PropTypes.string, // Libellé de voie
    occ_tit_18: PropTypes.string, // Lieu-dit / BP / Localité
    occ_tit_19: PropTypes.string, // Code postal
    occ_tit_20: PropTypes.string, // Commune
    occ_tit_21: PropTypes.string, // Téléphone portable
    occ_tit_22: PropTypes.string, // Adresse mail
    occ_tit_23: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Certification acquisition
    occ_tit_24: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Certification information administrative

    // Champs véhicule occasion - Co-titulaire
    occ_cotit_0: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Multi-propriété (0: non, 1: oui)
    occ_cotit_1: PropTypes.string, // Nombre de titulaires
    occ_cotit_2: PropTypes.oneOfType([
        PropTypes.oneOf(['0', '1']),
        PropTypes.oneOf([0, 1])
    ]), // Type (0: personne physique, 1: personne morale)
    occ_cotit_3: PropTypes.string, // Nom et prénom
    occ_cotit_4: PropTypes.string, // Nom d'usage
    occ_cotit_5: PropTypes.string, // Raison sociale
    occ_cotit_6: PropTypes.string, // N° SIRET
}); 